<template>
  <div :class="{ 'product-search-field': true, 'product-search-field--opened': isProductSearchFieldShowed }">
    <div
      :class="{
        'product-search-field__open-button': true,
        'product-search-field__open-button--inactive': isProductSearchFieldShowed,
      }"
      @click="
        isProductSearchFieldShowed = true;
        $refs.productSearchField.focus();
      "
      v-html="icons.singles.searchIcon"
    ></div>

    <fs-input
      ref="productSearchField"
      v-model="productSearchQuery"
      class="product-search-field__field-itself"
      placeholder="Search"
      is-search
      @change="
        (newValue) => {
          productSearchQuery = newValue;
        }
      "
      @focus="
        () => {
          if (productSearchQuery) {
            $refs.popperSearchResults.open({ target: $refs.productSearchField.$el });
          }
        }
      "
      @blur="isProductSearchFieldShowed = !!productSearchQuery"
      @keydown.tab="onTab"
      @keydown.down.prevent="moveHighlight(1)"
      @keydown.up.prevent="moveHighlight(-1)"
      @keydown.enter="openHighlighted"
      @keydown.esc="highlightedIndex = -1"
    />

    <fs-popper
      ref="popperSearchResults"
      :is-close-on-esc="true"
      :offset="[0, 30]"
      :prevent-overflow-padding="{ left: 30, right: 30 }"
      :close-on-outside-click-check="closeOnOutsideClickCheck"
    >
      <search-results
        ref="searchResults"
        :loading="isLoading"
        :loading-more="isLoadingMore"
        :has-more="hasMore"
        :search-results="searchResults"
        :close-and-clear="closeAndClear"
        :search-query="productSearchQuery"
        :highlighted-index="highlightedIndex"
        @load-more="loadMore"
      />
    </fs-popper>
  </div>
</template>

<script>
import lodashDebounce from 'lodash/debounce';
import { useUser } from '@/store/user';
import { useActiveSeason } from '@/store/active-season';
import { registerProductSearch, unregisterProductSearch } from '@lib/product-search';

// Components
import FsPopper from '@components/poppers/fs-popper.vue';
import FsInput from '@components/form-elements/fs-input.vue';
import SearchResults from '@components/search-results/index.vue';

// Icons
// singles
import searchIcon from '@icons/other/ico-search.svg';

export default {
  name: 'product-search-field',

  components: {
    FsPopper,
    FsInput,
    SearchResults,
  },

  data() {
    return {
      currentSearch: null,
      isLoading: false,
      isLoadingMore: false,
      isProductSearchFieldShowed: false,
      productSearchQuery: '',
      searchResults: [],
      highlightedIndex: -1,
      hasMore: false,
      pageSize: 6,

      icons: {
        singles: {
          searchIcon,
        },
      },
    };
  },

  created() {
    this.debouncedSearch = lodashDebounce(this.search, 300, { leading: true });
  },

  mounted() {
    document.body.addEventListener('focusin', this.closeSearchResultsOnBlur);
    registerProductSearch({ focus: () => this.expandAndFocus() });
  },

  beforeUnmount() {
    document.body.removeEventListener('focusin', this.closeSearchResultsOnBlur);
    unregisterProductSearch();
  },

  methods: {
    closeAndClear() {
      this.isProductSearchFieldShowed = false;
      this.productSearchQuery = '';
      this.highlightedIndex = -1;
    },

    moveHighlight(delta) {
      const count = this.searchResults.length;
      if (!count) return;

      this.highlightedIndex = (((this.highlightedIndex + delta) % count) + count) % count;
    },

    // Tab walks the results rather than leaving the header, but releases focus at either end of the list.
    onTab(event) {
      const count = this.searchResults.length;
      if (!count) return;

      const forward = !event.shiftKey;
      const atEdge = forward ? this.highlightedIndex === count - 1 : this.highlightedIndex === -1;

      if (atEdge) {
        this.highlightedIndex = -1;
        return;
      }

      event.preventDefault();

      if (!forward && this.highlightedIndex === 0) {
        this.highlightedIndex = -1;
        return;
      }

      this.highlightedIndex += forward ? 1 : -1;
    },

    openHighlighted(event) {
      if (this.highlightedIndex < 0) return;

      event.preventDefault();
      this.$refs.searchResults?.activateHighlighted();
    },

    // The `/` shortcut and command palette land here (via lib/product-search.ts).
    expandAndFocus() {
      this.isProductSearchFieldShowed = true;
      this.$refs.productSearchField.focus();
      this.$refs.productSearchField.select?.();
    },

    closeSearchResultsOnBlur(event) {
      const inputElement = this.$refs.productSearchField.$el;
      if (inputElement === event.target || inputElement.contains(event.target)) return;

      const resultsElement = this.$refs.searchResults?.$el;
      if (resultsElement === event.target || resultsElement?.contains(event.target)) return;

      this.$refs.popperSearchResults.close({ target: this.$refs.productSearchField.$el });
    },

    closeOnOutsideClickCheck() {
      const container = this.$refs.productSearchField;

      if (!container) return false;

      return container.$el.contains(document.activeElement);
    },

    search() {
      if (!this.productSearchQuery?.trim()) {
        return;
      }

      this.isLoading = true;

      const promise = this.$store.search.quickSearch(this.productSearchQuery, { take: this.pageSize });
      this.currentSearch = promise;

      this.currentSearch
        .then(({ searchResults }) => {
          // This is not the latest API call we've made, so let's skip this one and wait for the last one
          if (this.currentSearch !== promise) return;

          this.searchResults = searchResults;
          this.highlightedIndex = -1;
          this.hasMore = searchResults.length === this.pageSize;
          this.isLoading = false;
        })
        .catch((error) => {
          // This is not the latest API call we've made, so we don't really care if it errors
          if (this.currentSearch !== promise) return;

          throw error;
        });
    },

    loadMore() {
      if (this.isLoadingMore || !this.hasMore) return;

      this.isLoadingMore = true;
      const query = this.productSearchQuery;

      this.$api
        .search_QuickSearch({
          brandId: useUser().brandId,
          query,
          take: this.pageSize,
          skip: this.searchResults.length,
          activeSeasonId: useActiveSeason().activeSeasonId ?? undefined,
        })
        .then((moreResults) => {
          if (this.productSearchQuery !== query) return;
          this.searchResults = [...this.searchResults, ...moreResults];
          this.hasMore = moreResults.length === this.pageSize;
        })
        .catch(() => {})
        .finally(() => {
          this.isLoadingMore = false;
        });
    },
  },

  watch: {
    isProductSearchFieldShowed() {
      if (!this.isProductSearchFieldShowed) {
        this.$refs.popperSearchResults.close({ target: this.$refs.productSearchField.$el });
      }
    },

    productSearchQuery() {
      this.debouncedSearch();
      this.highlightedIndex = -1;

      if (this.productSearchQuery) {
        this.$refs.popperSearchResults.open({ target: this.$refs.productSearchField.$el });
      } else {
        this.$refs.popperSearchResults.close({ target: this.$refs.productSearchField.$el });
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import '@styles/variables.scss';

// ============================================================================
// Product search field
.product-search-field {
  // How far the input grows when opened. A custom property rather than a Sass
  // variable because navbar.vue reads it off this element to work out whether an
  // open field would reach the wordmark — see measureSearchClash there.
  --search-open-width: 19rem;

  // The layout box never changes size. Widening the root pushed everything
  // after it along the header — the nav shifted 225px and lost its labels just
  // because someone clicked search. Instead the field keeps its collapsed
  // footprint and the input expands out of it, anchored to the right edge, so
  // it opens leftward across the gap toward the logo and nothing else moves.
  width: 3.6rem;
  min-width: 3.6rem;
  height: 3.6rem;
  border-radius: 10rem;
  position: relative;
  background: #fff;

  &__open-button {
    width: 3.6rem;
    height: 3.6rem;
    border-radius: 10rem;
    display: flex;
    justify-content: center;
    align-content: center;
    align-items: center;
    position: absolute;
    inset: 0 auto auto 0;
    z-index: 2;
    opacity: 1;
    transition: opacity 0.1s ease;
    cursor: pointer;
    user-select: none;

    &--inactive {
      opacity: 0;
      pointer-events: none;
    }

    :deep(svg) {
      width: 1.4rem;
      height: 1.4rem;
      fill: rgba($c-dark, 1);
    }
  }

  &__field-itself {
    // Right-anchored and absolutely placed: this is what makes the growth go
    // leftward instead of displacing the header.
    position: absolute;
    inset: 0 0 auto auto;
    width: 3.6rem;
    height: 3.6rem;
    z-index: 1;
    background: #fff;
    transition: width 0.1s ease;

    &:hover {
      background: #fff;
    }
  }

  &--opened &__field-itself {
    width: var(--search-open-width);
  }
}
</style>
